import React, { useEffect, useState } from "react";
import { Form } from "react-bootstrap";

import type { ScreensaverChoice } from "./screensaverAssignmentModel";

type ExistingAsset = {
  source: "global" | "brand";
  sourceLabel: string;
  versionName: string | null;
  mediaUrl: string | null;
};

type Props = {
  mode: ScreensaverChoice;
  onModeChange: (mode: ScreensaverChoice) => void;
  fileName: string | null;
  onFile: (file: File | null) => void;
  existing: ExistingAsset | null;
  error: string | null;
};

const BrandScreensaverFields: React.FC<Props> = ({
  mode,
  onModeChange,
  fileName,
  onFile,
  existing,
  error,
}) => {
  const [localPreview, setLocalPreview] = useState<string | null>(null);

  useEffect(() => {
    return () => {
      if (localPreview) URL.revokeObjectURL(localPreview);
    };
  }, [localPreview]);

  const previewUrl =
    localPreview ||
    (mode === "custom" && existing?.source === "brand" ? existing.mediaUrl : null) ||
    (mode === "global" ? existing?.mediaUrl : null);

  return (
    <Form.Group className="mb-3" data-testid="brand-screensaver-fields">
      <Form.Label>Brand Screensaver</Form.Label>
      <Form.Check
        type="radio"
        name="brand-screensaver-mode"
        id="brand-screensaver-global"
        label="Use KrossKast Global Default"
        checked={mode === "global"}
        onChange={() => onModeChange("global")}
        data-testid="brand-screensaver-use-global"
      />
      <Form.Check
        type="radio"
        name="brand-screensaver-mode"
        id="brand-screensaver-custom"
        label="Upload Custom Brand Screensaver"
        checked={mode === "custom"}
        onChange={() => onModeChange("custom")}
        data-testid="brand-screensaver-use-custom"
      />
      {existing && (
        <Form.Text className="d-block mb-2" data-testid="brand-screensaver-source">
          {existing.sourceLabel}
          {existing.versionName ? ` · ${existing.versionName}` : ""}
        </Form.Text>
      )}
      {mode === "custom" && (
        <Form.Control
          className="mt-2"
          type="file"
          accept="video/mp4,.mp4"
          data-testid="brand-screensaver-file"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            const file = e.target.files?.[0] || null;
            if (localPreview) URL.revokeObjectURL(localPreview);
            setLocalPreview(file ? URL.createObjectURL(file) : null);
            onFile(file);
          }}
        />
      )}
      {fileName && (
        <Form.Text className="d-block" data-testid="brand-screensaver-filename">
          {fileName}
        </Form.Text>
      )}
      {previewUrl && (
        <video
          className="mt-2"
          data-testid="brand-screensaver-preview"
          src={previewUrl}
          controls
          muted
          width={320}
        />
      )}
      <div className="mt-2">
        <button
          type="button"
          className="btn btn-link btn-sm p-0"
          data-testid="brand-screensaver-reset"
          onClick={() => {
            if (localPreview) URL.revokeObjectURL(localPreview);
            setLocalPreview(null);
            onFile(null);
            onModeChange("global");
          }}
        >
          Reset to Global Default
        </button>
      </div>
      <Form.Text muted className="d-block">
        Optional. 1280×800, H.264, MP4, no audio. A failed video does not
        change the brand name, logo, industry, description, or status.
      </Form.Text>
      {error && (
        <div className="text-danger mt-2" data-testid="brand-screensaver-error">
          {error}
        </div>
      )}
    </Form.Group>
  );
};

export default BrandScreensaverFields;
